import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHabilitarObrasLote } from '@/hooks/useObras';
import { useTiposCasaSprints } from '@/hooks/useTiposCasa';
import type { IniciarObraLote } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const TIPOS: { value: IniciarObraLote['tipo_casa']; label: string }[] = [
  { value: 'auto', label: 'Automático (el de cada obra)' },
  { value: '1N-Techo', label: '1 Nivel — Techo' },
  { value: '1N-Azotea', label: '1 Nivel — Azotea' },
  { value: '2N-Techo', label: '2 Niveles — Techo' },
  { value: '2N-Azotea', label: '2 Niveles — Azotea' },
];

interface Props {
  /** Códigos seleccionados; si está vacío el diálogo está cerrado. */
  codigos: string[];
  abierto: boolean;
  onClose: () => void;
  /** Se llama tras habilitar con éxito (para limpiar la selección). */
  onHecho: () => void;
}

/** Diálogo admin para habilitar varias obras de una sola vez. */
export function HabilitarLoteDialog({ codigos, abierto, onClose, onHecho }: Props) {
  const mutation = useHabilitarObrasLote();
  const tiposSprintsQ = useTiposCasaSprints();
  const tiposSprints = tiposSprintsQ.data?.data ?? [];
  const [tipoCasa, setTipoCasa] = useState<IniciarObraLote['tipo_casa']>('auto');
  const [sprint, setSprint] = useState('1');

  // Sprints reales del tipo (si es 'auto'/mixto, rango completo 1..19).
  const sprintsDelTipo =
    tipoCasa !== 'auto' ? (tiposSprints.find((t) => t.tipo_casa === tipoCasa)?.sprints ?? []) : [];
  const opcionesSprint =
    sprintsDelTipo.length > 0 ? sprintsDelTipo : Array.from({ length: 19 }, (_, i) => i + 1);

  useEffect(() => {
    if (abierto) {
      setTipoCasa('auto');
      setSprint('1');
    }
  }, [abierto]);

  function handleOpenChange(open: boolean) {
    if (!open && !mutation.isPending) onClose();
  }

  async function handleHabilitar() {
    if (codigos.length === 0) return;
    try {
      const res = await mutation.mutateAsync({
        codigos,
        tipo_casa: tipoCasa,
        sprint_inicial: Number(sprint) || 1,
      });
      const r = res.data;
      if (r) {
        toast.success(
          `${r.habilitadas} obra${r.habilitadas === 1 ? '' : 's'} habilitada${r.habilitadas === 1 ? '' : 's'}`,
          {
            description:
              r.omitidas > 0
                ? `${r.omitidas} omitida${r.omitidas === 1 ? '' : 's'} (sin tipo de casa en modo automático).`
                : undefined,
          },
        );
      }
      onHecho();
      onClose();
    } catch (e) {
      toast.error('No se pudo habilitar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Habilitar {codigos.length} obras</DialogTitle>
          <DialogDescription>
            Se pondrán en ejecución para que el personal de campo registre avance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-xs">Tipo de casa</Label>
            <Select
              value={tipoCasa}
              onValueChange={(v) => setTipoCasa(v as IniciarObraLote['tipo_casa'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tipoCasa === 'auto' && (
              <p className="mt-1 text-xs text-muted-foreground">
                Usa el tipo derivado de BC de cada obra. Las que no lo tengan se omiten — asignáles
                un tipo concreto o configúralas una por una.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="lote-sprint" className="mb-1.5 block text-xs">
              Sprint inicial
            </Label>
            <Select value={sprint} onValueChange={setSprint}>
              <SelectTrigger id="lote-sprint">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {opcionesSprint.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    Sprint {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleHabilitar} disabled={mutation.isPending || codigos.length === 0}>
            {mutation.isPending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Habilitando…
              </>
            ) : (
              `Habilitar ${codigos.length}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
